import { useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIResultHoverStart, onAIResultHoverStop } from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common';

const document = getDocument();

// Timeout constants for hover behavior, these come from the people and teams profilecard package
const HOVER_START_DELAY = 800; // Delay before starting hover on trigger
const HOVER_END_DELAY = 250; // Delay before ending hover when mouse leaves elements

interface MeaningfulHoverProps {
	/** The type of element being hovered over (e.g., 'button', 'card', 'tooltip') */
	elementType: string;
	/** Optional callback fired when hover starts */
	onHoverStart?: () => void;
	/** Optional callback fired when hover ends, receives hover duration in milliseconds */
	onHoverStop?: (duration: number) => void;
	/** Query selector string to find the hover card element (e.g., '[data-testid="team-profilecard"]') */
	hoverCardSelector: string;
	/** Optional custom delay before starting hover on trigger (defaults to 800ms) */
	hoverStartDelay?: number;
	/** Optional custom delay before ending hover when mouse leaves elements (defaults to 250ms) */
	hoverEndDelay?: number;
}

/**
 * Hook for tracking meaningful hover interactions on UI components.
 *
 * This hook provides functionality to:
 * - Track when a user starts hovering over a component
 * - Measure the duration of the hover interaction
 * - Fire analytics events for hover start/stop (unless custom callbacks are provided)
 * - Maintain hover state for UI updates
 * - Prevents premature callback triggering during mouse movements between elements
 * - Handles continuous hover tracking between trigger and hover card
 *
 * @param props - Configuration object for the hover tracking
 * @param props.elementType - Identifier for the type of component being hovered
 * @param props.onHoverStart - Optional custom callback for hover start
 * @param props.onHoverStop - Optional custom callback for hover end with duration
 * @param props.hoverCardSelector - Query selector string to find the hover card element
 *
 * @returns Object containing:
 * - `isHovering`: Boolean indicating current hover state
 * - `handleHoverStart`: Function to call on mouse enter/hover start
 * - `handleHoverLeave`: Function to call on mouse leave/hover end
 */
export const useMeaningfulHover = ({
	elementType,
	onHoverStart,
	onHoverStop,
	hoverCardSelector,
	hoverStartDelay = HOVER_START_DELAY,
	hoverEndDelay = HOVER_END_DELAY,
}: MeaningfulHoverProps) => {
	const [fireAnalyticsEvent] = useAnalytics();
	const { commonAttributes } = useAIAnswerContext();
	const [isHovering, setIsHovering] = useState(false);
	const hoverStartTimeRef = useRef<number | null>(null);
	const isOnTriggerRef = useRef(false);
	const isOnHoverCardRef = useRef(false);

	/**
	 * Checks if the mouse is currently over the hover card element.
	 */
	const isMouseOnHoverCard = useCallback(
		(event: MouseEvent) => {
			if (!document) {
				return false;
			}

			const hoverCardElement = document.querySelector(hoverCardSelector);
			if (!hoverCardElement) {
				return false;
			}

			return hoverCardElement.contains(event.target as Node);
		},
		[hoverCardSelector],
	);

	/**
	 * Handles mouse movement to track position relative to hover card and manage hover state.
	 */
	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			const isOnHoverCard = isMouseOnHoverCard(event);
			isOnHoverCardRef.current = isOnHoverCard;

			// If mouse leaves hover card, check if we should end hover after delay
			if (!isOnHoverCard) {
				setTimeout(() => {
					// End hover if mouse is not on either element after delay
					if (!isOnHoverCardRef.current && hoverStartTimeRef.current && !isOnTriggerRef.current) {
						const hoverTime = Date.now() - hoverStartTimeRef.current;
						hoverStartTimeRef.current = null;

						if (onHoverStop) {
							onHoverStop(hoverTime);
						} else {
							fireAnalyticsEvent(
								onAIResultHoverStop({
									elementType,
									hoverTime,
									...commonAttributes,
								}),
							);
						}
						setIsHovering(false);
					}
				}, hoverEndDelay);
			}
		},
		[
			isMouseOnHoverCard,
			onHoverStop,
			fireAnalyticsEvent,
			commonAttributes,
			elementType,
			hoverEndDelay,
		],
	);

	/**
	 * Handles the start of a hover interaction on the trigger.
	 * Manages hover state and prevents duplicate hover interactions.
	 */
	const handleHoverStart = useCallback(
		(event: React.MouseEvent) => {
			// Don't start hover if mouse is already on hover card
			const isOnHoverCard = isMouseOnHoverCard(event.nativeEvent);
			if (isOnHoverCard) {
				return;
			}

			// Check if hover card is currently open
			let anyHoverCardOpen;
			if (document) {
				anyHoverCardOpen = document.querySelector(hoverCardSelector);
			}

			// If hover card is open and we're already hovering, continue existing hover
			if (anyHoverCardOpen && isHovering) {
				isOnTriggerRef.current = true;
				return;
			}

			// Don't start new hover if hover card is already open
			if (anyHoverCardOpen) {
				return;
			}

			isOnTriggerRef.current = true;

			// Start hover after delay to prevent premature triggering
			setTimeout(() => {
				// Only start hover if user is still on trigger after delay
				if (!isHovering && isOnTriggerRef.current) {
					const startTime = Date.now();
					hoverStartTimeRef.current = startTime;
					setIsHovering(true);

					if (onHoverStart) {
						onHoverStart();
					} else {
						fireAnalyticsEvent(
							onAIResultHoverStart({
								elementType,
								...commonAttributes,
							}),
						);
					}
				}
			}, hoverStartDelay);
		},
		[
			elementType,
			onHoverStart,
			fireAnalyticsEvent,
			commonAttributes,
			isHovering,
			isMouseOnHoverCard,
			hoverCardSelector,
			hoverStartDelay,
		],
	);

	/**
	 * Handles the end of a hover interaction on the trigger.
	 * Updates trigger state to track mouse position.
	 */
	const handleHoverLeave = useCallback(() => {
		isOnTriggerRef.current = false;
	}, []);

	// Set up global mouse move tracking
	useEffect(() => {
		if (!fg('kd_meaningful_hover') || !document) {
			return;
		}

		const removeMouseMoveListener = bind(document, {
			type: 'mousemove',
			listener: handleMouseMove,
		});

		return () => {
			removeMouseMoveListener();
		};
	}, [handleMouseMove]);

	return {
		isHovering,
		handleHoverStart,
		handleHoverLeave,
	};
};
